
import { mapGetters } from 'vuex';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  Workorder,
  CostLabor,
  CostsApi,
  User as PerformoUser,
  ValidationProblemDetails
} from 'performo-api';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';

import AddLaborComponent from '@/components/orders/AddLaborComponent.vue';
import DeleteCostLabor from '@/components/orders/DeleteCostLabor.vue';
import UpdateCostLabor from '@/components/orders/UpdateCostLabor.vue';

import { User } from 'oidc-client';
import { IPagedCollection } from 'performo-api';

@Component({
  components: {
    AddLaborComponent,
    DeleteCostLabor,
    UpdateCostLabor
  },
  computed: mapGetters({
    costsApi: 'costsApi',
    currentUser: 'currentUser',
    performoUser: 'performoUser'
  })
})
export default class OrderLaborListComponent extends Vue {
  @Prop({ required: true })
  private order!: Workorder;
  @Prop({ required: false, default: false, type: Boolean })
  private required!: boolean;
  @Prop({ required: false, default: 100 })
  private laborEntriesPerPage!: number;

  private loading = false;
  private costLabors: CostLabor[] = [];
  private costLaborPagination?: IPagedCollection;
  private performoUser!: PerformoUser;
  private currentPage = 0;

  private currentUser!: User;
  private costsApi!: CostsApi;

  async mounted() {
    await this.fetchCostLabors();
  }

  @Watch('order')
  async updateLabor() {
    await this.fetchCostLabors();
  }

  async fetchCostLabors() {
    this.loading = true;
    try {
      let response = await this.costsApi.getWorkOrderCostLabor({
        workOrderId: this.order.workorderId ?? undefined,
        skip: this.currentPage * this.laborEntriesPerPage,
        take: this.laborEntriesPerPage
      });
      this.costLabors = response.data ?? [];
      this.costLaborPagination = response.pagination;
    } catch (error) {
      console.error('Error getting cost labor for work order: ', error);
    }
    this.loading = false;
  }

  private async errorHandler(error: ValidationProblemDetails | Response) {
    this.$emit('error', error);
  }

  get costLaborDisplayOptions(): ColumnDisplayOption[] {
    return [
      { displayName: 'Labor/Trade', columnName: 'laborName', dontSort: true },
      {
        displayName: 'Labor Date',
        columnName: 'laborDate',
        dateFormatKind: 1,
        dontSort: true
      },
      {
        displayName: 'Hours',
        columnName: 'laborHours',
        width: '50px',
        dontSort: true,
        formatter: (f: number) => {
          if (f) return f.toFixed(2);
          else return '';
        }
      },
      {
        displayName: 'Labor Note',
        columnName: 'tcardNotes',
        dontSort: true
      },
      {
        displayName: 'Actions',
        columnName: 'laborAction',
        width: '50px',
        dontSort: true
      }
    ];
  }

  private async laborPaginationUpdatedHandler(pageNumber: number) {
    this.currentPage = pageNumber - 1; // one-based paging from handler to zero-based paging in component
    await this.fetchCostLabors();
  }

  private async laborAddedHandler(labor: CostLabor) {
    this.$emit('labor-added', labor);
    await this.fetchCostLabors();
  }

  private async laborUpdatedHandler() {
    this.$emit('labor-updated');
    await this.fetchCostLabors();
  }

  private async laborDeletedHandler() {
    this.$emit('labor-deleted');
    await this.fetchCostLabors();
  }
}
